import React from 'react';
import { Space } from 'antd';

export const defaultTableProps = {
  data: [],
  loading: true,
  pagination: undefined,
};

/**
 * Creates a reusable loadTableData function.
 * fetchFn should accept an object: { query, successCallback, errorCallback }
 * getQueryParams should be a function (key) => params
 * transformResults (optional) should receive response data and return array of items
 */
export const createLoadTableData = (fetchFn, getQueryParams, transformResults = (data) => data?.results || data?.invoices || []) => {
  return ({ key, setter, query, initial = false } = {}) => {
    setter?.(prev => ({ ...prev, loading: true }));

    fetchFn({
      query: { ...getQueryParams(key), ...query },
      successCallback: (data) => {
        const results = transformResults(data) || [];
        setter?.(prev => ({
          ...prev,
          data: initial ? results : [...(prev.data || []), ...results],
          loading: false,
          pagination: data.pagination
        }));
      },
      errorCallback: () => {
        setter?.(prev => ({ ...prev, loading: false, pagination: undefined }));
      }
    });
  };
};

/**
 * Returns a JSX block with Table and optional see more button.
 * TableComponent - the Table component to render
 * SeeMoreButton - component used for "see more" (expects loading, onClick, children props)
 */
export const getTableJSX = ({
  TableComponent,
  columns,
  tableProps,
  onRowClick,
  SeeMoreButton,
  handleLoadMore,
  keyName,
  t,
  showSeeMore = true,
}) => {
  const cursor = tableProps?.pagination?.cursor;
  const isAvailableSeeMore = showSeeMore && cursor;

  return (
    <Space size='small' direction='vertical'>
      <TableComponent
        columns={columns}
        pagination={false}
        dataSource={tableProps?.data || []}
        onRow={(record) => ({ onClick: () => onRowClick(record) })}
        rowKey='id'
      />
      {isAvailableSeeMore && (
        <SeeMoreButton
          loading={tableProps?.loading}
          onClick={() => handleLoadMore(keyName, cursor)}
          type='link'
        >
          {t('see')} {t('more')}
        </SeeMoreButton>
      )}
    </Space>
  );
};

export const getLabelFactory = (t, Icon) => (key, namespace = 'overviewPage.budgets') => (
  <Space size='small' align='center'>
    <Icon />
    <span>{t(`${namespace}.${key}`)}</span>
  </Space>
);

const overviewShared = {
  defaultTableProps,
  createLoadTableData,
  getTableJSX,
  getLabelFactory,
};

export default overviewShared;
